import { useState, useEffect } from 'react';
import { CampaignMetrics, OverviewMetricsData } from '../types';

export interface UseCampaignMetricsReturn {
  metrics: CampaignMetrics | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useCampaignMetrics(
  campaignId: number,
  fetchMetrics: (campaignId: number) => Promise<CampaignMetrics>
): UseCampaignMetricsReturn {
  const [metrics, setMetrics] = useState<CampaignMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchMetrics(campaignId);
      setMetrics(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch campaign metrics'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refetch();
  }, [campaignId]);

  return { metrics, loading, error, refetch };
}

export interface UseOverviewMetricsOptions {
  startDate?: string;
  endDate?: string;
}

export interface UseOverviewMetricsReturn {
  metrics: OverviewMetricsData | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useOverviewMetrics(
  fetchMetrics: (options?: UseOverviewMetricsOptions) => Promise<OverviewMetricsData>,
  options?: UseOverviewMetricsOptions
): UseOverviewMetricsReturn {
  const [metrics, setMetrics] = useState<OverviewMetricsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchMetrics(options);
      setMetrics(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch overview metrics'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refetch();
  }, [options?.startDate, options?.endDate]);

  return { metrics, loading, error, refetch };
}

